import { ColDef, ColGroupDef } from "ag-grid-community";
import { useCallback, useMemo } from "react";
import { AgGridDateCellEditor } from "../AgGridDateCellEditor";
import { formatDateToDisplay, fromOADate } from "../AgGridContext";
import { currencyFormatter, VALIDATION_ERRORS_KEY } from "../AgGridUtils";

interface UseColumnDefsParams {
  isEditMode: boolean;
  defaultColDef?: ColDef;
}

export const transformRowData = (rawRows: Record<string, any>[]): Record<string, any>[] =>
  rawRows.map((row) => {
    const flatRow: Record<string, any> = {};
    for (const key of Object.keys(row)) {
      const cell = row[key];
      flatRow[key] =
        cell !== null && typeof cell === "object" && "value" in cell ? cell.value : cell;
    }
    return flatRow;
  });

export const useColumnDefs = ({ isEditMode, defaultColDef }: UseColumnDefsParams) => {
  const transformColumnDefs = useCallback(
    (
      rawDefs: any[],
      mainColumnDefs: Record<string, string[]> = {},
    ): (ColDef | ColGroupDef)[] => {
      const addClass = (existing: any, newClass: string) => [
        ...(Array.isArray(existing) ? existing : existing ? [existing] : []),
        newClass,
      ];
      const groupableFields = new Set(Object.values(mainColumnDefs).flat());

      const processCol = (col: any): ColDef | ColGroupDef => {
        if (col.children) {
          return {
            ...col,
            headerStyle: col.bgColor
              ? { backgroundColor: "rgb(68 62 62 / 0%)", color: "#ffffff" }
              : undefined,
            children: col.children.map(processCol),
          };
        }

        const colType = (col.type || "").toLowerCase();
        const isInputType = colType === "input";
        const isDataValidation = colType.startsWith("data validation");
        const isEditable = isEditMode && (isInputType || isDataValidation);
        const isMandatory = Boolean(col.required || col.isMandatory);
        const isEmptyHeader = !col.headerName || col.headerName.trim() === "";
        const numericTypes = [
          "data validation decimal",
          "data validation percentage",
          "data validation currency",
          "calculation currency",
          "calculation decimal",
          "calculation percentage",
        ];
        const isNumericType = numericTypes.includes(colType);

        const transformed: ColDef = {
          field: col.field,
          headerName: col.headerName,
          headerTooltip: col.headerName,
          type: col.type,
          editable: isEditable,
          ...(isEmptyHeader ? { minWidth: 52, width: 52 } : {}),
          ...(col.required !== undefined ? { required: col.required } : {}),
          ...(col.isMandatory !== undefined ? { isMandatory: col.isMandatory } : {}),
          headerStyle: col.bgColor
            ? { backgroundColor: "rgb(68 62 62 / 0%)", color: "#ffffff" }
            : undefined,
          headerClass:
            [
              isMandatory ? "mandatory-column-header" : null,
              groupableFields.has(col.field) ? "groupable-column-header" : null,
            ]
              .filter(Boolean)
              .join(" ") || undefined,
          cellClassRules: {
            "mandatory-cell-error": (params: any) =>
              Boolean(
                params.data?.[VALIDATION_ERRORS_KEY]?.[params.colDef.field as string],
              ),
          },
        };

        if (colType === "hide" || colType === "end") {
          transformed.hide = true;
        }
        if (isNumericType) {
          transformed.cellClass = addClass(transformed.cellClass, "ag-right-aligned-cell");
        }
        if (isEditMode && !isInputType && !isDataValidation) {
          transformed.cellClass = addClass(transformed.cellClass, "non-editable-cell");
        }

        if (isEditMode && isDataValidation && col.dataValidationList) {
          const values = col.dataValidationList.split(",").map((v: string) => v.trim());
          transformed.cellEditor = "agRichSelectCellEditor";
          transformed.cellEditorParams = { values, cellHeight: 30, searchDebounceDelay: 300 };
          transformed.singleClickEdit = true;
          transformed.cellRenderer = (params: any) => (
            <span style={{ display: "flex", alignItems: "center", width: "100%" }}>
              <span style={{ flex: 1 }}>{params.value}</span>
              <span style={{ fontSize: "10px", color: "#888" }}>&#9660;</span>
            </span>
          );
        }

        if (colType === "data validation date") {
          transformed.editable = isEditable;
          transformed.singleClickEdit = true;
          transformed.cellEditor = AgGridDateCellEditor;
          transformed.cellEditorPopup = true;
          transformed.valueGetter = (params: any) => {
            const raw = params.data?.[col.field];
            if (raw == null || raw === "") return null;
            const num = Number(raw);
            if (!isNaN(num)) return fromOADate(num);
            const parsed = new Date(raw);
            if (!isNaN(parsed.getTime())) return parsed;
            return null;
          };
          transformed.valueFormatter = (params: any) => {
            if (!params.value) return "";
            return formatDateToDisplay(params.value);
          };
          transformed.valueSetter = (params: any) => {
            if (!params.newValue) return false;
            const date =
              params.newValue instanceof Date ? params.newValue : new Date(params.newValue);
            if (isNaN(date.getTime())) return false;
            const oaEpoch = new Date(1899, 11, 30);
            const oaDate = (date.getTime() - oaEpoch.getTime()) / 86400000;
            params.data[col.field] = oaDate;
            return true;
          };
        }

        transformed.enableRowGroup = groupableFields.has(col.field);

        if (isEditMode && colType === "data validation decimal") {
          transformed.cellEditor = "agNumberCellEditor";
          transformed.valueParser = (params: any) => {
            const val = Number(params.newValue);
            if (isNaN(val)) return null;
            return val;
          };
        }

        if (isEditMode && colType === "data validation percentage") {
          transformed.cellEditor = "agNumberCellEditor";
          transformed.cellEditorParams = { min: 0, max: 100, step: 0.000001, precision: 6 };
          transformed.valueParser = (params: any) => {
            const val = Number(params.newValue);
            if (isNaN(val) || val < 0) return null;
            return val > 1 ? val / 100 : val;
          };
        }

        if (colType === "data validation currency" || colType === "calculation currency") {
          transformed.valueFormatter = (params: any) =>
            currencyFormatter(params.value, "$", true);
        }

        if (colType === "data validation percentage" || colType === "calculation percentage") {
          transformed.valueFormatter = (params: any) => {
            if (params.value == null || params.value === "") return "";
            return `${parseFloat((Number(params.value) * 100).toFixed(6))}%`;
          };
        }

        if (colType === "data validation decimal" || colType === "calculation decimal") {
          transformed.valueFormatter = (params: any) =>
            currencyFormatter(params.value, "", true);
        }

        return transformed;
      };

      return rawDefs.map(processCol);
    },
    [isEditMode],
  );

  const mergeColumnDefsWithMeta = useCallback(
    (
      columnDefs: (ColDef | ColGroupDef)[] | undefined,
    ): (ColDef | ColGroupDef)[] | undefined => {
      if (!columnDefs) return columnDefs;

      const mapDefs = (defs: (ColDef | ColGroupDef)[]): (ColDef | ColGroupDef)[] =>
        defs.map((def) => {
          if ("children" in def && def.children) {
            return { ...def, children: mapDefs(def.children) };
          }
          const leafDef = def as ColDef & { required?: boolean; isMandatory?: boolean };
          const isMandatory = Boolean(leafDef.required || leafDef.isMandatory);
          return {
            ...leafDef,
            headerClass: (params: any) => {
              const base =
                typeof leafDef.headerClass === "function"
                  ? leafDef.headerClass(params)
                  : leafDef.headerClass;
              const classes: string[] = Array.isArray(base)
                ? [...base]
                : typeof base === "string"
                  ? [base]
                  : [];
              if (isMandatory) classes.push("mandatory-column-header");
              if (leafDef.enableRowGroup) classes.push("groupable-column-header");
              return classes.length > 0 ? classes : undefined;
            },
            cellClassRules: {
              ...(leafDef.cellClassRules || {}),
              "mandatory-cell-error": (params: any) =>
                Boolean(
                  params.data?.[VALIDATION_ERRORS_KEY]?.[params.colDef.field as string],
                ),
            },
          };
        });

      return mapDefs(columnDefs as (ColDef | ColGroupDef)[]);
    },
    [],
  );

  const mergedDefaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      wrapHeaderText: false,
      filter: "agSetColumnFilter",
      headerStyle: { "background-color": "transparent" },
      tooltipValueGetter: (params: any) => (params.value != null ? String(params.value) : ""),
      headerTooltip: undefined,
      ...defaultColDef,
    }),
    [defaultColDef],
  );

  return { transformColumnDefs, mergeColumnDefsWithMeta, mergedDefaultColDef };
};
